import { FC } from 'react';
import { Modal } from 'react-bootstrap';
import ImgRem from '../../libs/imgRem';
import { FtuxStep } from './types';

interface FtuxModalProps {
	step: FtuxStep;
	currentStepIndex: number;
	totalSteps: number;
	paginatedStepIndex: number | null;
	paginatedStepTotal: number | null;
	show: boolean;
	onNext: () => void;
	hasBackButton?: boolean;
	onBack: () => void;
	hasBackdrop: boolean;
}

const FtuxModal: FC<FtuxModalProps> = ({
	step,
	currentStepIndex,
	totalSteps,
	paginatedStepIndex,
	paginatedStepTotal,
	show,
	onNext,
	onBack,
	hasBackButton = true,
	hasBackdrop
}) => {
	const isFirstStep = currentStepIndex === 0;
	const isLastStep = currentStepIndex === totalSteps - 1;
	const buttonType = step.button?.type || 'full-width';
	const buttonLabel = step.button?.label || (isLastStep ? 'Done' : 'Next');
	const displayCurrentStep = paginatedStepIndex ?? currentStepIndex + 1;
	const displayTotalSteps = paginatedStepTotal ?? totalSteps;

	// Determine modal positioning classes based on modalPosition
	const getModalClassName = () => {
		const baseClass = 'modal-ftux';
		if (!step.modalPosition || step.modalPosition === 'center') {
			return `${baseClass} modal-ftux-center`;
		}
		return `${baseClass} modal-ftux-${step.modalPosition}`;
	};

	return (
		<Modal
			show={show}
			className={getModalClassName()}
			centered={!step.modalPosition || step.modalPosition === 'center'}
			backdrop={hasBackdrop}
			keyboard={false}
			restoreFocus={false}
		>
			<Modal.Body>
				<div className="ftux-modal-content">
					{/* Header Image (for welcome/first step) */}
					{step.headerImage && (
						<div className="ftux-modal-header-image">
							<ImgRem src={step.headerImage} alt="FTUX illustration" width="160" height="160" />
						</div>
					)}

					{/* Content Image */}
					{step.image && !step.headerImage && (
						<div className="ftux-modal-image">
							<img src={step.image} alt={step.title || 'FTUX step'} />
						</div>
					)}

					{/* Text Content */}
					<div className="ftux-modal-text">
						{step.title && (
							<h3 className={`ftux-modal-title ${isFirstStep && step.headerImage ? 'text-center' : 'text-start'}`}>
								{step.title}
							</h3>
						)}
						{step.subtitle && (
							<p
								className={`ftux-modal-subtitle ${isFirstStep && step.headerImage ? 'text-center' : 'text-start'}`}
								dangerouslySetInnerHTML={{ __html: step.subtitle }}
							/>
						)}
					</div>

					{/* Buttons */}
					<div className="ftux-modal-buttons">
						{buttonType === 'full-width' ? (
							<>
								<button
									type="button"
									onClick={() => {
										if (step.button?.action) {
											step.button.action();
										}
										onNext();
									}}
									className="btn btn-primary btn-lg w-100"
								>
									{buttonLabel}
								</button>
							</>
						) : (
							<div className="ftux-modal-pagination">
								<div className="ftux-pagination-counter">
									<span className="current">{displayCurrentStep}</span>
									<span className="separator">/</span>
									<span className="total">{displayTotalSteps}</span>
								</div>
								<div className="ftux-pagination-buttons">
									{hasBackButton && (
										<button type="button" onClick={onBack} className="btn btn-outline btn-sm">
											Back
										</button>
									)}
									<button
										type="button"
										onClick={() => {
											if (step.button?.action) {
												step.button.action();
											}
											onNext();
										}}
										className="btn btn-primary btn-sm"
									>
										{buttonLabel}
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default FtuxModal;
